import { Plus, Trash2 } from "lucide-react";
import React, { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "../../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import {
	COMPARISON_OPTIONS,
	LakeQueryResponse,
	ReferenceLine,
	ReportWidget,
	Threshold,
	WidgetCompareSetting,
	WidgetDisplay,
} from "../../../models/ReportBuilder";
import { PALETTE } from "./WidgetChart";
import { formatNumber, isNumericColumn } from "./format";

interface WidgetSettingsDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	widget: ReportWidget;
	/** Latest rows, for column pickers and the format preview. */
	data?: LakeQueryResponse;
	onSave: (display: WidgetDisplay) => void;
}

const THRESHOLD_COLORS: { value: Threshold["color"]; label: string; className: string }[] = [
	{ value: "green", label: "Grön", className: "bg-brand" },
	{ value: "amber", label: "Gul", className: "bg-warning" },
	{ value: "red", label: "Röd", className: "bg-danger" },
	{ value: "gray", label: "Grå", className: "bg-gray-400" },
];

const field =
	"w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-brand focus:outline-none";
const label = "mb-1 block text-xs font-medium text-gray-600";

const Toggle: React.FC<{
	checked: boolean;
	onChange: (v: boolean) => void;
	children: React.ReactNode;
}> = ({ checked, onChange, children }) => (
	<label className="flex cursor-pointer items-center gap-2 text-sm text-gray-800">
		<input
			type="checkbox"
			checked={checked}
			onChange={(e) => onChange(e.target.checked)}
			className="h-4 w-4 rounded border-solid border-gray-300"
		/>
		{children}
	</label>
);

const numberOrUndefined = (text: string): number | undefined => {
	const n = Number(text.replace(",", "."));
	return text.trim() === "" || Number.isNaN(n) ? undefined : n;
};

/**
 * Everything about a widget that is not the query: text, number format,
 * thresholds, sort/limit, axes, reference lines, comparison and columns.
 * Saved as `display` beside the spec, so a regenerate keeps it.
 */
const WidgetSettingsDialog: React.FC<WidgetSettingsDialogProps> = ({
	open,
	onOpenChange,
	widget,
	data,
	onSave,
}) => {
	const [display, setDisplay] = useState<WidgetDisplay>(widget.display ?? {});

	useEffect(() => {
		if (open) setDisplay(widget.display ?? {});
	}, [open, widget.id, widget.display]);

	const patch = (p: Partial<WidgetDisplay>) => setDisplay((d) => ({ ...d, ...p }));
	const columns = data?.columns ?? [];
	const numericColumns = data ? columns.filter((c) => isNumericColumn(data.rows, c)) : [];
	const sample = data?.rows[0]?.[numericColumns[0] ?? ""] ?? 12345.678;
	const chartType = widget.spec.chart.type;
	const isSeries = ["bar", "line", "area", "combo"].includes(chartType);
	const seriesKeys = widget.spec.chart.yKeys?.length ? widget.spec.chart.yKeys : numericColumns;
	const thresholds = display.thresholds ?? [];
	const references = display.referenceLines ?? [];

	const setThreshold = (i: number, t: Partial<Threshold>) =>
		patch({ thresholds: thresholds.map((x, j) => (j === i ? { ...x, ...t } : x)) });
	const setReference = (i: number, r: Partial<ReferenceLine>) =>
		patch({ referenceLines: references.map((x, j) => (j === i ? { ...x, ...r } : x)) });

	const toggleHidden = (c: string, hidden: boolean) => {
		const set = new Set(display.columns?.hidden ?? []);
		hidden ? set.add(c) : set.delete(c);
		patch({ columns: { ...(display.columns ?? {}), hidden: [...set] } });
	};
	const moveColumn = (c: string, direction: -1 | 1) => {
		const order = display.columns?.order?.length ? [...display.columns.order] : [...columns];
		const i = order.indexOf(c);
		const j = i + direction;
		if (i < 0 || j < 0 || j >= order.length) return;
		[order[i], order[j]] = [order[j], order[i]];
		patch({ columns: { ...(display.columns ?? {}), order } });
	};
	const orderedColumns = display.columns?.order?.length
		? [
				...display.columns.order.filter((c) => columns.includes(c)),
				...columns.filter((c) => !display.columns!.order!.includes(c)),
		  ]
		: columns;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="z-[1300] flex max-h-[90vh] w-[min(44rem,calc(100vw-2rem))] max-w-none flex-col overflow-hidden">
				<DialogHeader>
					<DialogTitle>Widgetinställningar</DialogTitle>
					<DialogDescription>
						Utseende och text. Frågan ändras med prompt i widgetmenyn.
					</DialogDescription>
				</DialogHeader>

				<Tabs defaultValue="text" className="flex min-h-0 flex-1 flex-col">
					<TabsList className="flex flex-wrap justify-start">
						<TabsTrigger value="text">Text</TabsTrigger>
						<TabsTrigger value="format">Format</TabsTrigger>
						<TabsTrigger value="thresholds">Trösklar</TabsTrigger>
						<TabsTrigger value="rows">Sortering</TabsTrigger>
						{isSeries && <TabsTrigger value="axis">Axlar</TabsTrigger>}
						{chartType === "table" && <TabsTrigger value="columns">Kolumner</TabsTrigger>}
						<TabsTrigger value="compare">Jämförelse</TabsTrigger>
					</TabsList>

					<div className="min-h-0 flex-1 overflow-y-auto pr-1 pt-3">
						<TabsContent value="text" className="mt-0 space-y-3">
							<div>
								<span className={label}>Rubrik</span>
								<input
									className={field}
									value={display.title ?? ""}
									placeholder={widget.spec.title}
									onChange={(e) => patch({ title: e.target.value || undefined })}
								/>
							</div>
							<div>
								<span className={label}>Underrubrik</span>
								<input
									className={field}
									value={display.description ?? ""}
									placeholder={widget.spec.description ?? "Ingen underrubrik"}
									onChange={(e) => patch({ description: e.target.value || undefined })}
								/>
								<p className="mt-1 text-[11px] text-gray-500">
									Tomt fält visar den genererade texten. Skriv ett mellanslag för att dölja
									underrubriken.
								</p>
							</div>
							<div>
								<span className={label}>Enhet</span>
								<input
									className={field}
									value={display.unit ?? ""}
									placeholder={widget.spec.chart.unit ?? "t.ex. kr, timmar, %"}
									onChange={(e) => patch({ unit: e.target.value || undefined })}
								/>
							</div>
						</TabsContent>

						<TabsContent value="format" className="mt-0 space-y-3">
							<div className="grid grid-cols-2 gap-3">
								<div>
									<span className={label}>Decimaler</span>
									<Select
										value={
											display.format?.decimals === undefined
												? "auto"
												: String(display.format.decimals)
										}
										onValueChange={(v) =>
											patch({
												format: {
													...(display.format ?? {}),
													decimals: v === "auto" ? undefined : Number(v),
												},
											})
										}
									>
										<SelectTrigger>
											<SelectValue />
										</SelectTrigger>
										<SelectContent className="z-[1300]">
											<SelectItem value="auto">Automatiskt</SelectItem>
											{[0, 1, 2, 3].map((n) => (
												<SelectItem key={n} value={String(n)}>
													{n}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
								<div className="flex items-end pb-2">
									<Toggle
										checked={!!display.format?.compact}
										onChange={(v) =>
											patch({ format: { ...(display.format ?? {}), compact: v || undefined } })
										}
									>
										Kompakt (1,2 mkr)
									</Toggle>
								</div>
								<div>
									<span className={label}>Prefix</span>
									<input
										className={field}
										value={display.format?.prefix ?? ""}
										placeholder="t.ex. ≈"
										onChange={(e) =>
											patch({
												format: { ...(display.format ?? {}), prefix: e.target.value || undefined },
											})
										}
									/>
								</div>
								<div>
									<span className={label}>Suffix (ersätter enheten)</span>
									<input
										className={field}
										value={display.format?.suffix ?? ""}
										placeholder="t.ex. kr/h"
										onChange={(e) =>
											patch({
												format: { ...(display.format ?? {}), suffix: e.target.value || undefined },
											})
										}
									/>
								</div>
							</div>
							<p className="text-xs text-gray-600">
								Exempel:{" "}
								<span className="font-medium text-gray-900">
									{formatNumber(
										sample,
										display.unit?.trim() || widget.spec.chart.unit,
										display.format
									)}
								</span>
							</p>
						</TabsContent>

						<TabsContent value="thresholds" className="mt-0 space-y-3">
							<p className="text-xs text-gray-600">
								Färgar nyckeltalet och tabellceller. Den högsta gränsen värdet når vinner; värden
								under alla gränser förblir ofärgade.
							</p>
							{thresholds.map((t, i) => (
								<div key={i} className="flex items-center gap-2">
									<span className="text-xs text-gray-500">från</span>
									<input
										className={cn(field, "w-32")}
										value={String(t.from)}
										onChange={(e) =>
											setThreshold(i, { from: numberOrUndefined(e.target.value) ?? 0 })
										}
										inputMode="decimal"
									/>
									<div className="flex items-center gap-1">
										{THRESHOLD_COLORS.map((c) => (
											<button
												key={c.value}
												type="button"
												title={c.label}
												onClick={() => setThreshold(i, { color: c.value })}
												className={cn(
													"h-6 w-6 rounded-full border-2 border-solid",
													c.className,
													t.color === c.value ? "border-gray-900" : "border-transparent"
												)}
											/>
										))}
									</div>
									<button
										type="button"
										onClick={() => patch({ thresholds: thresholds.filter((_, j) => j !== i) })}
										className="ml-auto rounded p-1 text-gray-400 hover:text-danger"
										aria-label="Ta bort tröskel"
									>
										<Trash2 className="h-4 w-4" />
									</button>
								</div>
							))}
							<button
								type="button"
								onClick={() =>
									patch({
										thresholds: [
											...thresholds,
											{
												from: thresholds.length
													? Math.max(...thresholds.map((t) => t.from)) * 2 || 100
													: 0,
												color: thresholds.length ? "green" : "red",
											},
										],
									})
								}
								className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
							>
								<Plus className="h-3 w-3" /> Lägg till tröskel
							</button>
							{chartType === "table" && numericColumns.length > 0 && (
								<div>
									<span className={label}>Kolumner som färgas</span>
									<div className="flex flex-wrap gap-3">
										{numericColumns.map((c) => {
											const chosen = display.thresholdColumns ?? [];
											const on = chosen.length ? chosen.includes(c) : true;
											return (
												<Toggle
													key={c}
													checked={on}
													onChange={(v) => {
														const base = chosen.length ? chosen : numericColumns;
														const next = v
															? [...new Set([...base, c])]
															: base.filter((x) => x !== c);
														patch({
															thresholdColumns:
																next.length === numericColumns.length ? undefined : next,
														});
													}}
												>
													{c}
												</Toggle>
											);
										})}
									</div>
								</div>
							)}
						</TabsContent>

						<TabsContent value="rows" className="mt-0 space-y-3">
							<div className="grid grid-cols-2 gap-3">
								<div>
									<span className={label}>Sortera på</span>
									<Select
										value={display.sort?.key ?? "__none"}
										onValueChange={(v) =>
											patch({
												sort:
													v === "__none"
														? undefined
														: { key: v, direction: display.sort?.direction ?? "desc" },
											})
										}
									>
										<SelectTrigger>
											<SelectValue />
										</SelectTrigger>
										<SelectContent className="z-[1300]">
											<SelectItem value="__none">Som frågan ger</SelectItem>
											{columns.map((c) => (
												<SelectItem key={c} value={c}>
													{c}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
								<div>
									<span className={label}>Riktning</span>
									<Select
										value={display.sort?.direction ?? "desc"}
										onValueChange={(v) =>
											display.sort &&
											patch({ sort: { ...display.sort, direction: v as "asc" | "desc" } })
										}
									>
										<SelectTrigger disabled={!display.sort}>
											<SelectValue />
										</SelectTrigger>
										<SelectContent className="z-[1300]">
											<SelectItem value="desc">Fallande</SelectItem>
											<SelectItem value="asc">Stigande</SelectItem>
										</SelectContent>
									</Select>
								</div>
								<div>
									<span className={label}>Visa högst</span>
									<input
										className={field}
										inputMode="numeric"
										value={display.limit?.n ?? ""}
										placeholder="alla rader"
										onChange={(e) => {
											const n = numberOrUndefined(e.target.value);
											patch({
												limit:
													n && n > 0 ? { n: Math.floor(n), rest: display.limit?.rest } : undefined,
											});
										}}
									/>
								</div>
								<div className="flex items-end pb-2">
									<Toggle
										checked={!!display.limit?.rest}
										onChange={(v) =>
											display.limit && patch({ limit: { ...display.limit, rest: v || undefined } })
										}
									>
										Resten som ”Övriga”
									</Toggle>
								</div>
							</div>
						</TabsContent>

						{isSeries && (
							<TabsContent value="axis" className="mt-0 space-y-3">
								<div className="grid grid-cols-2 gap-2">
									<Toggle
										checked={display.axis?.yFromZero !== false}
										onChange={(v) =>
											patch({ axis: { ...(display.axis ?? {}), yFromZero: v ? undefined : false } })
										}
									>
										Y-axel från noll
									</Toggle>
									<Toggle
										checked={!!display.axis?.log}
										onChange={(v) =>
											patch({ axis: { ...(display.axis ?? {}), log: v || undefined } })
										}
									>
										Logaritmisk y-axel
									</Toggle>
									<Toggle
										checked={!!display.axis?.hideLegend}
										onChange={(v) =>
											patch({ axis: { ...(display.axis ?? {}), hideLegend: v || undefined } })
										}
									>
										Dölj förklaring
									</Toggle>
									<Toggle
										checked={!!display.axis?.rotateLabels}
										onChange={(v) =>
											patch({ axis: { ...(display.axis ?? {}), rotateLabels: v || undefined } })
										}
									>
										Lutande etiketter
									</Toggle>
									<Toggle
										checked={!!display.axis?.average}
										onChange={(v) =>
											patch({ axis: { ...(display.axis ?? {}), average: v || undefined } })
										}
									>
										Snittlinje
									</Toggle>
								</div>
								<div>
									<span className={label}>Seriefärger</span>
									<div className="space-y-1">
										{seriesKeys.map((k, i) => (
											<div key={k} className="flex items-center gap-2 text-sm">
												<input
													type="color"
													value={display.axis?.colors?.[k] ?? PALETTE[i % PALETTE.length]}
													onChange={(e) =>
														patch({
															axis: {
																...(display.axis ?? {}),
																colors: { ...(display.axis?.colors ?? {}), [k]: e.target.value },
															},
														})
													}
													className="h-6 w-8 cursor-pointer rounded border border-solid border-gray-300 bg-white p-0"
												/>
												<span>{k}</span>
											</div>
										))}
									</div>
								</div>
								<div>
									<span className={label}>Referenslinjer</span>
									{references.map((r, i) => (
										<div key={i} className="mb-1 flex items-center gap-2">
											<input
												className={cn(field, "w-28")}
												inputMode="decimal"
												value={String(r.value)}
												onChange={(e) =>
													setReference(i, { value: numberOrUndefined(e.target.value) ?? 0 })
												}
											/>
											<input
												className={field}
												value={r.label ?? ""}
												placeholder="Etikett, t.ex. Budget"
												onChange={(e) => setReference(i, { label: e.target.value || undefined })}
											/>
											<input
												type="color"
												value={r.color ?? "#D64545"}
												onChange={(e) => setReference(i, { color: e.target.value })}
												className="h-6 w-8 cursor-pointer rounded border border-solid border-gray-300 bg-white p-0"
											/>
											<button
												type="button"
												onClick={() =>
													patch({ referenceLines: references.filter((_, j) => j !== i) })
												}
												className="rounded p-1 text-gray-400 hover:text-danger"
												aria-label="Ta bort linje"
											>
												<Trash2 className="h-4 w-4" />
											</button>
										</div>
									))}
									<button
										type="button"
										onClick={() =>
											patch({ referenceLines: [...references, { value: 0, label: "Mål" }] })
										}
										className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
									>
										<Plus className="h-3 w-3" /> Lägg till linje
									</button>
								</div>
							</TabsContent>
						)}

						{chartType === "table" && (
							<TabsContent value="columns" className="mt-0 space-y-1">
								{orderedColumns.length === 0 && (
									<p className="text-xs text-gray-500">
										Kör widgeten först så att kolumnerna syns här.
									</p>
								)}
								{orderedColumns.map((c) => (
									<div
										key={c}
										className="flex items-center gap-2 rounded px-1 py-0.5 hover:bg-gray-50"
									>
										<Toggle
											checked={!(display.columns?.hidden ?? []).includes(c)}
											onChange={(v) => toggleHidden(c, !v)}
										>
											{c}
										</Toggle>
										<span className="ml-auto flex gap-1">
											<button
												type="button"
												onClick={() => moveColumn(c, -1)}
												className="rounded px-1 text-xs text-gray-500 hover:bg-gray-100"
											>
												↑
											</button>
											<button
												type="button"
												onClick={() => moveColumn(c, 1)}
												className="rounded px-1 text-xs text-gray-500 hover:bg-gray-100"
											>
												↓
											</button>
										</span>
									</div>
								))}
							</TabsContent>
						)}

						<TabsContent value="compare" className="mt-0 space-y-3">
							<span className={label}>Jämförelse för den här widgeten</span>
							<Select
								value={display.compare ?? "report"}
								onValueChange={(v) =>
									patch({ compare: v === "report" ? undefined : (v as WidgetCompareSetting) })
								}
							>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent className="z-[1300]">
									<SelectItem value="report">Följ dashboarden</SelectItem>
									{COMPARISON_OPTIONS.map((o) => (
										<SelectItem key={o.value} value={o.value}>
											{o.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</TabsContent>
					</div>
				</Tabs>

				<DialogFooter className="items-center gap-2">
					<button
						type="button"
						onClick={() => setDisplay({})}
						className="mr-auto text-xs text-gray-500 hover:text-gray-800"
					>
						Återställ allt
					</button>
					<button
						type="button"
						onClick={() => onOpenChange(false)}
						className="rounded-md border px-3 py-1.5 text-sm"
					>
						Avbryt
					</button>
					<button
						type="button"
						onClick={() => {
							onSave(display);
							onOpenChange(false);
						}}
						className="rounded-md bg-gray-900 px-3 py-1.5 text-sm font-medium text-white"
					>
						Spara
					</button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default WidgetSettingsDialog;
